import React, { useState, useEffect } from 'react';
import {
  HeartIcon,
  PhotoIcon,
  UserIcon,
  IdentificationIcon,
  TagIcon,
  HomeIcon,
  ShieldCheckIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import ConfirmationModal from './ConfirmationModal';
import AttributeLabel from './AttributeLabel';
import apiClient from '../utils/apiClient';
import { getDogImageUrl, shouldUseImageUrls } from '../utils/imageUtils';
import { ModalShell, ModalActions } from './modals/core';
import type { ModalCloseReason } from './modals/core';

interface Dog {
  dog_id?: number;
  hundename: string;
  vollstaendiger_name?: string | null;
  rasse?: string | null;
  geburtstag?: string | null;
  chipnummer?: string | null;
  tassonummer?: string | null;
  geschlecht?: string | number | null;
  kastriert: boolean | number | string;
  image_path?: string | null;
  image_path_updated?: string | null;
  hundebox?: string | number | null;
  insurance_name?: string | null;
  tollwut_end_date?: string | null;
}

interface AddEditDogModalProps {
  isOpen: boolean;
  onClose: () => void;
  dog?: Dog | null;
  memberId?: number;
  memberName?: string;
  onSuccess: () => void;
}

interface ConfirmModalState {
  isOpen: boolean;
  type?: 'success' | 'warning' | 'error' | 'question' | 'info';
  title: string;
  message: string;
  variant?: 'single' | 'two';
  onConfirm?: () => void;
}

const AddEditDogModal: React.FC<AddEditDogModalProps> = ({
  isOpen,
  onClose,
  dog,
  memberId,
  memberName,
  onSuccess
}) => {
  const [formData, setFormData] = useState<Dog>({
    hundename: '',
    vollstaendiger_name: '',
    rasse: '',
    geburtstag: '',
    chipnummer: '',
    tassonummer: '',
    geschlecht: '',
    kastriert: false,
    hundebox: '',
    insurance_name: '',
    tollwut_end_date: ''
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({ isOpen: false, title: '', message: '' });

  const isEditing = !!dog;

  useEffect(() => {
    if (isOpen) {
      if (dog) {
        // Editing existing dog
        setFormData({
          hundename: dog.hundename || '',
          vollstaendiger_name: dog.vollstaendiger_name || '',
          rasse: dog.rasse || '',
          geburtstag: dog.geburtstag || '',
          chipnummer: dog.chipnummer || '',
          tassonummer: dog.tassonummer || '',
          geschlecht: dog.geschlecht === 0 || dog.geschlecht === 'männlich' || dog.geschlecht === 'Männlich' ? 'Männlich' :
                      dog.geschlecht === 1 || dog.geschlecht === 'weiblich' || dog.geschlecht === 'Weiblich' ? 'Weiblich' : '',
          kastriert: dog.kastriert === true || dog.kastriert === 1 || dog.kastriert === 'true',
          hundebox: dog.hundebox === 1 || dog.hundebox === 'ja' ? 'ja' :
                    dog.hundebox === 0 || dog.hundebox === 'nein' ? 'nein' : 
                    String(dog.hundebox || ''),
          insurance_name: dog.insurance_name || '',
          tollwut_end_date: dog.tollwut_end_date || ''
        });
        // Set image preview with new API or fallback to storage
        if (dog.image_path) {
          if (dog.dog_id && shouldUseImageUrls()) {
            setImagePreview(getDogImageUrl(dog.dog_id, 'M', dog.image_path_updated || undefined));
          } else {
            const baseUrl = localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';
            setImagePreview(`${baseUrl}/storage/${dog.image_path}`);
          }
          setImageError(false); // Reset image error when setting new image
        } else {
          setImagePreview(null);
          setImageError(false);
        }
      } else {
        // Adding new dog
        setFormData({
          hundename: '',
          vollstaendiger_name: '',
          rasse: '',
          geburtstag: '',
          chipnummer: '',
          tassonummer: '',
          geschlecht: '',
          kastriert: false,
          hundebox: '',
          insurance_name: '',
          tollwut_end_date: ''
        });
        setImagePreview(null);
        setImageError(false);
      }
      setSelectedImage(null);
    }
  }, [isOpen, dog]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      setImageError(false); // Reset error when new image is selected
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!formData.hundename.trim()) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Bitte geben Sie einen Rufnamen für den Hund ein.',
        variant: 'single'
      });
      return;
    }

    try {
      setIsLoading(true);
      
      const submitFormData = new FormData();
      submitFormData.append('rufname', formData.hundename);
      submitFormData.append('vollstaendiger_name', formData.vollstaendiger_name || '');
      submitFormData.append('rasse', formData.rasse || '');
      submitFormData.append('geburtstag', formData.geburtstag || '');
      submitFormData.append('chipnummer', formData.chipnummer || '');
      submitFormData.append('tassonummer', formData.tassonummer || '');
      
      // Convert geschlecht to 0 for männlich, 1 for weiblich, null for not specified
      if (formData.geschlecht === 'Männlich' || formData.geschlecht === 'männlich') {
        submitFormData.append('geschlecht', '0');
      } else if (formData.geschlecht === 'Weiblich' || formData.geschlecht === 'weiblich') {
        submitFormData.append('geschlecht', '1');
      } else if (formData.geschlecht) {
        // If it's already a number, keep it as is
        submitFormData.append('geschlecht', String(formData.geschlecht));
      }
      
      // Convert kastriert to 1/0/null for MySQL backend
      if (formData.kastriert === true || formData.kastriert === 1 || formData.kastriert === 'true') {
        submitFormData.append('kastriert', '1');
      } else if (formData.kastriert === false || formData.kastriert === 0 || formData.kastriert === 'false') {
        submitFormData.append('kastriert', '0');
      }
      
      // Convert hundebox to 1/0/null for MySQL backend
      if (formData.hundebox === 'ja' || formData.hundebox === 'Ja' || formData.hundebox === 1) {
        submitFormData.append('hundebox', '1');
      } else if (formData.hundebox === 'nein' || formData.hundebox === 'Nein' || formData.hundebox === 0) {
        submitFormData.append('hundebox', '0');
      }
      
      submitFormData.append('insurance_name', formData.insurance_name || '');
      submitFormData.append('impfstatus_tollwut', formData.tollwut_end_date || '');
      
      if (selectedImage) {
        submitFormData.append('image', selectedImage);
      }
      
      if (!isEditing && memberId) {
        submitFormData.append('member_id', memberId.toString());
      }

      const url = isEditing ? `/api/dogs/${dog!.dog_id}` : '/api/dogs/add_dog';
      
      let response;
      if (isEditing) {
        response = await apiClient.put(url, submitFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        response = await apiClient.post(url, submitFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      if (response.data.success) {
        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Erfolgreich',
          message: `Hund wurde erfolgreich ${isEditing ? 'aktualisiert' : 'hinzugefügt'}.`,
          variant: 'single'
        });
        onSuccess();
        onClose();
      } else {
        throw new Error('API Error');
      }
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: `Der Hund konnte nicht ${isEditing ? 'aktualisiert' : 'hinzugefügt'} werden.`,
        variant: 'single'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (_reason: ModalCloseReason) => {
    onClose();
  };

  return (
    <>
      <ModalShell
        open={isOpen}
        onClose={handleClose}
        variant="form"
        size="md"
        title={isEditing ? 'Hund bearbeiten' : 'Neuen Hund hinzufügen'}
        subtitle={memberName ? `Mitglied: ${memberName}` : undefined}
        icon={HeartIcon}
        closeOnBackdrop={false}
        bodyClassName="!p-0"
        footer={
          <ModalActions
            secondary={{ label: 'Abbrechen', onClick: onClose }}
            primary={{
              label: isEditing ? 'Änderungen speichern' : 'Hund hinzufügen',
              onClick: () => handleSubmit(),
              loading: isLoading,
              icon: <HeartIcon className="h-4 w-4" />,
            }}
          />
        }
      >
        {/* Image Upload - Full Width like DogCard */}
        <div className="relative h-48 sm:h-56">
          {imagePreview && !imageError ? (
            <img
              src={imagePreview}
              alt="Hund"
              className="w-full h-full object-cover"
              onError={() => {
                // Show fallback icon immediately if image loading fails
                setImageError(true);
              }}
            />
          ) : (
            <div className="w-full h-full bg-primary-600 flex items-center justify-center">
              <HeartIcon className="h-20 w-20 text-white/70" />
            </div>
          )}
          
          {/* Upload Button Overlay */}
          <div className="absolute bottom-4 right-4">
            <label className="cursor-pointer">
              <span className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/90 dark:bg-gray-900/90 backdrop-blur text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-white dark:hover:bg-gray-900 transition-colors shadow-lg text-sm font-medium">
                <PhotoIcon className="h-4 w-4 flex-shrink-0" />
                <span>{selectedImage ? 'Bild ändern' : 'Bild auswählen'}</span>
              </span>
              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleImageChange}
              />
            </label>
          </div>
          
          {/* Selected Image Indicator */}
          {selectedImage && (
            <div className="absolute top-4 right-4 bg-success-500 text-white rounded-full p-2 shadow-lg">
              <CheckCircleIcon className="h-5 w-5" />
            </div>
          )}
          
          {/* Selected Filename */}
          {selectedImage && (
            <div className="absolute top-4 left-4 bg-black/50 backdrop-blur text-white px-3 py-1.5 rounded-lg text-xs">
              Neues Bild: {selectedImage.name}
            </div>
          )}
        </div>

        <div className="p-6 space-y-6">

          {/* Grunddaten */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <UserIcon className="h-5 w-5 text-primary-600" />
              Grunddaten
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Rufname */}
              <div>
                <AttributeLabel iconKey="name" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Rufname *
                </AttributeLabel>
                <input
                  type="text"
                  name="hundename"
                  value={formData.hundename}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
                  required
                />
              </div>

              {/* Vollständiger Name */}
              <div>
                <AttributeLabel iconKey="name" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Vollständiger Name
                </AttributeLabel>
                <input
                  type="text"
                  name="vollstaendiger_name"
                  value={formData.vollstaendiger_name || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              {/* Rasse */}
              <div>
                <AttributeLabel iconKey="breed" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Rasse
                </AttributeLabel>
                <input
                  type="text"
                  name="rasse"
                  value={formData.rasse || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              {/* Geburtstag */}
              <div>
                <AttributeLabel iconKey="birthday" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Geburtstag
                </AttributeLabel>
                <input
                  type="date"
                  name="geburtstag"
                  value={formData.geburtstag || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              {/* Geschlecht */}
              <div>
                <AttributeLabel iconKey="gender" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Geschlecht
                </AttributeLabel>
                <select
                  name="geschlecht"
                  value={formData.geschlecht || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
                >
                  <option value="">Bitte wählen</option>
                  <option value="Männlich">Männlich</option>
                  <option value="Weiblich">Weiblich</option>
                </select>
              </div>

              {/* Kastriert */}
              <div className="sm:col-span-2">
                <div className="bg-warning-50 dark:bg-warning-900/20 rounded-lg p-3 border border-warning-200 dark:border-warning-800/50">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="kastriert"
                      checked={Boolean(formData.kastriert === true || formData.kastriert === 1 || formData.kastriert === 'true')}
                      onChange={handleInputChange}
                      className="h-5 w-5 text-warning-600 focus:ring-warning-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <AttributeLabel iconKey="neutered" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Hund ist kastriert
                    </AttributeLabel>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Kennzeichnung */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <IdentificationIcon className="h-5 w-5 text-primary-600" />
              Kennzeichnung
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Chipnummer */}
              <div>
                <AttributeLabel iconKey="chipNumber" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Chipnummer
                </AttributeLabel>
                <input
                  type="text"
                  name="chipnummer"
                  value={formData.chipnummer || ''}
                  onChange={handleInputChange}
                  placeholder="15-stellige Nummer"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              {/* Tassonummer */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <TagIcon className="h-4 w-4 text-warning-500" />
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Tassonummer
                  </label>
                </div>
                <input
                  type="text"
                  name="tassonummer"
                  value={formData.tassonummer || ''}
                  onChange={handleInputChange}
                  placeholder="T123456789"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Haltung */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <HomeIcon className="h-5 w-5 text-primary-600" />
              Haltung
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Hundebox */}
              <div>
                <AttributeLabel iconKey="dogBox" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Hundebox
                </AttributeLabel>
                <select
                  name="hundebox"
                  value={formData.hundebox || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
                >
                  <option value="">Bitte wählen</option>
                  <option value="ja">Ja</option>
                  <option value="nein">Nein</option>
                </select>
              </div>
            </div>
          </div>

          {/* Versicherung & Gesundheit */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border-2 border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <ShieldCheckIcon className="h-5 w-5 text-primary-600" />
              Versicherung & Gesundheit
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Versicherung */}
              <div>
                <AttributeLabel iconKey="liability" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Haftpflichtversicherung
                </AttributeLabel>
                <input
                  type="text"
                  name="insurance_name"
                  value={formData.insurance_name || ''}
                  onChange={handleInputChange}
                  placeholder="z.B. HUK, Helvetia, Allianz"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              {/* Tollwut Gültig bis */}
              <div>
                <AttributeLabel iconKey="vaccination" className="block mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tollwutimpfung gültig bis
                </AttributeLabel>
                <input
                  type="date"
                  name="tollwut_end_date"
                  value={formData.tollwut_end_date || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>
        </div>
      </ModalShell>

      <ConfirmationModal
        visible={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, isOpen: false })) } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: { text: 'Abbrechen', onPress: () => setConfirmModal(prev => ({ ...prev, isOpen: false })) },
          confirm: { text: 'Bestätigen', onPress: () => { confirmModal.onConfirm?.(); setConfirmModal(prev => ({ ...prev, isOpen: false })); } }
        } : undefined}
      />
    </>
  );
};

export default AddEditDogModal;